import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import AppLayout from "./components/Layout/AppLayout";
import Login from "./pages/Login";
import "./App.css";

// ─── Lazy-loaded pages ──────────────────────────────────────────────────
const SuperAdminDashboard = React.lazy(() => import("./pages/superadmin/Dashboard"));
const Users = React.lazy(() => import("./pages/superadmin/Users"));
const Proposals = React.lazy(() => import("./pages/superadmin/Proposals"));
const Settings = React.lazy(() => import("./pages/superadmin/Settings"));
const Analytics = React.lazy(() => import("./pages/superadmin/Analytics"));
const AuditLogs = React.lazy(() => import("./pages/superadmin/AuditLogs"));
const SMSLogs = React.lazy(() => import("./pages/superadmin/SMSLogs"));
const AgentDashboard = React.lazy(() => import("./pages/agent/Dashboard"));
const CustomerList = React.lazy(() => import("./pages/shared/CustomerList"));
const CustomerProfile = React.lazy(() => import("./pages/shared/CustomerProfile"));
const LoanDetails = React.lazy(() => import("./pages/shared/LoanDetails"));
const NewCustomer = React.lazy(() => import("./pages/agent/NewCustomer"));
const CustomerDashboard = React.lazy(() => import("./pages/customer/CustomerDashboard"));
const CustomerEMISchedule = React.lazy(() => import("./pages/customer/CustomerEMISchedule"));
const CustomerStatements = React.lazy(() => import("./pages/customer/CustomerStatements"));
const CustomerPayEMI = React.lazy(() => import("./pages/customer/CustomerPayEMI"));
const AdminDashboard = React.lazy(() => import("./pages/admin/Dashboard"));
const CollectionsList = React.lazy(() => import("./pages/shared/CollectionsList"));
const PaymentChannels = React.lazy(() => import("./pages/superadmin/PaymentChannels"));
const AgentPerformance = React.lazy(() => import("./pages/superadmin/AgentPerformance"));
const CollectionIntelligence = React.lazy(() => import("./pages/shared/CollectionIntelligence"));
const CustomerLedger = React.lazy(() => import("./pages/customer/CustomerLedger"));
const PaymentPage = React.lazy(() => import("./pages/customer/PaymentPage"));
const MyPayments = React.lazy(() => import("./pages/customer/MyPayments"));
const PaymentVerification = React.lazy(() => import("./pages/admin/PaymentVerification"));
const ChartOfAccounts = React.lazy(() => import("./pages/accounting/ChartOfAccounts"));
const AccBankAccounts = React.lazy(() => import("./pages/accounting/BankAccounts"));
const JournalEntries = React.lazy(() => import("./pages/accounting/JournalEntries"));
const AccLedger = React.lazy(() => import("./pages/accounting/Ledger"));
const ProfitLoss = React.lazy(() => import("./pages/accounting/ProfitLoss"));
const BalanceSheet = React.lazy(() => import("./pages/accounting/BalanceSheet"));
const BankStatement = React.lazy(() => import("./pages/accounting/BankStatement"));
const AccAuditLogs = React.lazy(() => import("./pages/accounting/AuditLogs"));
const Exports = React.lazy(() => import("./pages/accounting/Exports"));
const LoanPortfolio = React.lazy(() => import("./pages/accounting/LoanPortfolio"));
const ChangePassword = React.lazy(() => import("./pages/shared/ChangePassword"));
const BulkImport = React.lazy(() => import("./pages/shared/BulkImport"));
const TrainingCenter = React.lazy(() => import("./pages/shared/TrainingCenter"));
const OverdueWaivers = React.lazy(() => import("./pages/superadmin/OverdueWaivers"));
const InterestOnlyAccounts = React.lazy(() => import("./pages/superadmin/InterestOnlyAccounts"));
const PartPaymentControls = React.lazy(() => import("./pages/superadmin/PartPaymentControls"));
const AccountingDashboard = React.lazy(() => import("./pages/accounting/AccountingDashboard"));
const CashAccounts = React.lazy(() => import("./pages/accounting/CashAccounts"));
const Disbursements = React.lazy(() => import("./pages/accounting/Disbursements"));
const ImportBatches = React.lazy(() => import("./pages/admin/ImportBatches"));
const ImportBatchDetail = React.lazy(() => import("./pages/admin/ImportBatchDetail"));
const DataQuality = React.lazy(() => import("./pages/admin/DataQuality"));
const CommissionSettings = React.lazy(() => import("./pages/superadmin/CommissionSettings"));
const CommissionLedger = React.lazy(() => import("./pages/superadmin/CommissionLedger"));
const AgentCommissions = React.lazy(() => import("./pages/agent/AgentCommissions"));
const Leaderboard = React.lazy(() => import("./pages/shared/Leaderboard"));
const SystemHealth = React.lazy(() => import("./pages/superadmin/SystemHealth"));
const DuplicateResolution = React.lazy(() => import("./pages/superadmin/DuplicateResolution"));
const AgentLedger = React.lazy(() => import("./pages/shared/AgentLedger"));
const CustomerLedgerPage = React.lazy(() => import("./pages/shared/CustomerLedgerPage"));

const PageLoader = () => (
  <div className="p-4 md:p-6 space-y-4" data-testid="page-loader">
    <div className="flex items-center gap-3">
      <div className="h-5 w-5 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
      <div className="h-3 w-32 bg-slate-200 rounded animate-pulse" />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {[1,2,3,4].map(i => (
        <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
          <div className="h-3 w-16 bg-slate-100 rounded animate-pulse" />
          <div className="h-6 w-20 bg-slate-200 rounded animate-pulse" />
        </div>
      ))}
    </div>
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
      {[1,2,3].map(i => <div key={i} className="h-3 bg-slate-100 rounded animate-pulse" style={{width: `${50+i*15}%`}} />)}
    </div>
  </div>
);

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
          <p className="text-sm text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app/dashboard" replace />;
  return children;
};

const RoleRedirect = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "customer") return <Navigate to="/app/customer-portal" replace />;
  return <Navigate to="/app/dashboard" replace />;
};

function AppRoutes() {
  const { user } = useAuth();

  const getDashboard = () => {
    if (!user) return <Navigate to="/login" />;
    if (user.role === "superadmin") return <SuperAdminDashboard />;
    if (user.role === "admin") return <AdminDashboard />;
    if (user.role === "agent") return <AgentDashboard />;
    return <Navigate to="/app/customer-portal" />;
  };

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<RoleRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={getDashboard()} />
        <Route
          path="users"
          element={
            <ProtectedRoute roles={["superadmin", "admin"]}>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route path="customers" element={<CustomerList />} />
        <Route
          path="customers/new"
          element={
            <ProtectedRoute roles={["agent", "admin", "superadmin"]}>
              <NewCustomer />
            </ProtectedRoute>
          }
        />
        <Route path="customers/:id" element={<CustomerProfile />} />
        <Route path="loans/:id" element={<LoanDetails />} />
        <Route
          path="proposals"
          element={
            <ProtectedRoute roles={["superadmin", "admin", "agent"]}>
              <Proposals />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute roles={["superadmin"]}>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="analytics"
          element={
            <ProtectedRoute roles={["superadmin", "admin"]}>
              <Analytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="audit"
          element={
            <ProtectedRoute roles={["superadmin", "admin"]}>
              <AuditLogs />
            </ProtectedRoute>
          }
        />
        <Route
          path="sms"
          element={
            <ProtectedRoute roles={["superadmin", "admin"]}>
              <SMSLogs />
            </ProtectedRoute>
          }
        />
        <Route
          path="collections"
          element={
            <ProtectedRoute roles={["superadmin", "admin"]}>
              <CollectionsList />
            </ProtectedRoute>
          }
        />
        <Route
          path="payment-verification"
          element={
            <ProtectedRoute roles={["superadmin", "admin"]}>
              <PaymentVerification />
            </ProtectedRoute>
          }
        />
        <Route
          path="payment-channels"
          element={
            <ProtectedRoute roles={["superadmin"]}>
              <PaymentChannels />
            </ProtectedRoute>
          }
        />
        <Route
          path="overdue-waivers"
          element={
            <ProtectedRoute roles={["superadmin"]}>
              <OverdueWaivers />
            </ProtectedRoute>
          }
        />
        <Route
          path="interest-only-accounts"
          element={
            <ProtectedRoute roles={["superadmin"]}>
              <InterestOnlyAccounts />
            </ProtectedRoute>
          }
        />
        <Route
          path="part-payment-controls"
          element={
            <ProtectedRoute roles={["superadmin"]}>
              <PartPaymentControls />
            </ProtectedRoute>
          }
        />
        <Route
          path="accounting-dashboard"
          element={
            <ProtectedRoute roles={["superadmin", "admin"]}>
              <AccountingDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="cash-accounts"
          element={
            <ProtectedRoute roles={["superadmin", "admin"]}>
              <CashAccounts />
            </ProtectedRoute>
          }
        />
        <Route
          path="disbursements"
          element={
            <ProtectedRoute roles={["superadmin"]}>
              <Disbursements />
            </ProtectedRoute>
          }
        />
        <Route
          path="collection-intelligence"
          element={
            <ProtectedRoute roles={["superadmin", "admin", "agent"]}>
              <CollectionIntelligence />
            </ProtectedRoute>
          }
        />
        <Route
          path="agent-performance"
          element={
            <ProtectedRoute roles={["superadmin", "admin"]}>
              <AgentPerformance />
            </ProtectedRoute>
          }
        />
        <Route
          path="customer-portal"
          element={
            <ProtectedRoute roles={["customer"]}>
              <CustomerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="customer-portal/schedule"
          element={
            <ProtectedRoute roles={["customer"]}>
              <CustomerEMISchedule />
            </ProtectedRoute>
          }
        />
        <Route
          path="customer-portal/statements"
          element={
            <ProtectedRoute roles={["customer"]}>
              <CustomerStatements />
            </ProtectedRoute>
          }
        />
        <Route
          path="customer-portal/pay"
          element={
            <ProtectedRoute roles={["customer"]}>
              <CustomerPayEMI />
            </ProtectedRoute>
          }
        />
        <Route
          path="customer-portal/ledger"
          element={
            <ProtectedRoute roles={["customer"]}>
              <CustomerLedger />
            </ProtectedRoute>
          }
        />
        <Route
          path="customer-portal/payment"
          element={
            <ProtectedRoute roles={["customer"]}>
              <PaymentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="customer-portal/my-payments"
          element={
            <ProtectedRoute roles={["customer"]}>
              <MyPayments />
            </ProtectedRoute>
          }
        />
        {/* Accounting Suite - Admin/SuperAdmin only */}
        <Route path="accounting/chart-of-accounts" element={<ProtectedRoute roles={["superadmin", "admin"]}><ChartOfAccounts /></ProtectedRoute>} />
        <Route path="accounting/bank-accounts" element={<ProtectedRoute roles={["superadmin", "admin"]}><AccBankAccounts /></ProtectedRoute>} />
        <Route path="bank-accounts" element={<ProtectedRoute roles={["superadmin", "admin"]}><AccBankAccounts /></ProtectedRoute>} />
        <Route path="accounting/journal-entries" element={<ProtectedRoute roles={["superadmin", "admin"]}><JournalEntries /></ProtectedRoute>} />
        <Route path="journal-entries" element={<ProtectedRoute roles={["superadmin", "admin"]}><JournalEntries /></ProtectedRoute>} />
        <Route path="accounting/ledger" element={<ProtectedRoute roles={["superadmin", "admin"]}><AccLedger /></ProtectedRoute>} />
        <Route path="accounting/pnl" element={<ProtectedRoute roles={["superadmin", "admin"]}><ProfitLoss /></ProtectedRoute>} />
        <Route path="accounting/balance-sheet" element={<ProtectedRoute roles={["superadmin", "admin"]}><BalanceSheet /></ProtectedRoute>} />
        <Route path="accounting/bank-statement" element={<ProtectedRoute roles={["superadmin", "admin"]}><BankStatement /></ProtectedRoute>} />
        <Route path="accounting/exports" element={<ProtectedRoute roles={["superadmin", "admin"]}><Exports /></ProtectedRoute>} />
        <Route path="accounting/audit-logs" element={<ProtectedRoute roles={["superadmin"]}><AccAuditLogs /></ProtectedRoute>} />
        {/* Loan Portfolio Dashboard - separate section */}
        <Route path="portfolio" element={<ProtectedRoute roles={["superadmin", "admin"]}><LoanPortfolio /></ProtectedRoute>} />
        {/* Change Password - all roles */}
        <Route path="change-password" element={<ChangePassword />} />
        {/* Bulk Import - Admin/SuperAdmin */}
        <Route path="bulk-import" element={<ProtectedRoute roles={["superadmin", "admin", "agent"]}><BulkImport /></ProtectedRoute>} />
        {/* Training Center - All roles */}
        <Route path="training" element={<TrainingCenter />} />
        {/* Import Staging - SuperAdmin only */}
        <Route path="import-batches" element={<ProtectedRoute roles={["superadmin"]}><ImportBatches /></ProtectedRoute>} />
        <Route path="import-batches/:batchId" element={<ProtectedRoute roles={["superadmin"]}><ImportBatchDetail /></ProtectedRoute>} />
        <Route path="data-quality" element={<ProtectedRoute roles={["superadmin", "admin"]}><DataQuality /></ProtectedRoute>} />
        <Route path="commission-settings" element={<ProtectedRoute roles={["superadmin"]}><CommissionSettings /></ProtectedRoute>} />
        <Route path="commission-ledger" element={<ProtectedRoute roles={["superadmin", "admin"]}><CommissionLedger /></ProtectedRoute>} />
        <Route path="my-commissions" element={<ProtectedRoute roles={["agent"]}><AgentCommissions /></ProtectedRoute>} />
        <Route path="leaderboard" element={<ProtectedRoute roles={["superadmin", "admin", "agent"]}><Leaderboard /></ProtectedRoute>} />
        <Route path="system-health" element={<ProtectedRoute roles={["superadmin"]}><SystemHealth /></ProtectedRoute>} />
        <Route path="duplicate-resolution" element={<ProtectedRoute roles={["superadmin"]}><DuplicateResolution /></ProtectedRoute>} />
        <Route path="agent-ledger" element={<ProtectedRoute roles={["superadmin", "admin", "agent"]}><AgentLedger /></ProtectedRoute>} />
        <Route path="customer-ledger" element={<ProtectedRoute roles={["superadmin", "admin"]}><CustomerLedgerPage /></ProtectedRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
